import * as React from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import {
  BriefcaseMedical,
  Building2,
  CalendarDays,
  ChartColumn,
  ClipboardList,
  Eraser,
  FileSignature,
  FileText,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Network,
  Plug,
  Share2,
  ScrollText,
  Stethoscope,
  TriangleAlert,
  Users,
  UserSearch,
  Workflow,
  X,
} from 'lucide-react';

import { useAuth } from '@/features/auth/contexto';
import { ROTULOS_PERFIL, type PerfilUsuario } from '@/features/auth/tipos';
import { SemConexao } from '@/components/sem-conexao';
import { Botao } from '@/components/ui/button';
import { Etiqueta } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ItemMenu {
  para: string;
  rotulo: string;
  icone: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  perfis: PerfilUsuario[];
}

const MENU: ItemMenu[] = [
  {
    para: '/',
    rotulo: 'Início',
    icone: LayoutDashboard,
    perfis: ['UBS', 'REGULACAO', 'GESTOR', 'ADMINISTRADOR'],
  },
  {
    para: '/solicitacoes',
    rotulo: 'Solicitações',
    icone: FileText,
    perfis: ['UBS', 'REGULACAO', 'GESTOR'],
  },
  {
    para: '/demanda-reprimida',
    rotulo: 'Demanda reprimida',
    // `History` e não `Archive`: arquivo sugere caso encerrado, e estes pedidos
    // estão vivos na fila — é justamente por isso que foram cadastrados.
    icone: History,
    perfis: ['UBS', 'REGULACAO', 'GESTOR', 'ADMINISTRADOR'],
  },
  {
    para: '/historico-paciente',
    rotulo: 'Histórico Paciente',
    icone: UserSearch,
    perfis: ['UBS', 'REGULACAO', 'GESTOR', 'ADMINISTRADOR'],
  },
  {
    para: '/relatorios',
    rotulo: 'Relatórios',
    icone: ChartColumn,
    perfis: ['GESTOR', 'ADMINISTRADOR'],
  },
  { para: '/admin/usuarios', rotulo: 'Usuários', icone: Users, perfis: ['ADMINISTRADOR'] },
  {
    para: '/admin/ubs',
    rotulo: 'UBS',
    icone: Building2,
    perfis: ['ADMINISTRADOR', 'REGULACAO'],
  },
  {
    para: '/admin/especialidades',
    rotulo: 'Especialidades',
    icone: ListChecks,
    perfis: ['ADMINISTRADOR'],
  },
  {
    para: '/admin/procedimentos',
    rotulo: 'Procedimentos',
    icone: ClipboardList,
    perfis: ['ADMINISTRADOR'],
  },
  {
    para: '/admin/locais',
    rotulo: 'Locais de atendimento',
    icone: MapPin,
    perfis: ['ADMINISTRADOR'],
  },
  {
    para: '/admin/profissionais',
    rotulo: 'Profissionais',
    icone: BriefcaseMedical,
    perfis: ['ADMINISTRADOR'],
  },
  { para: '/admin/pec', rotulo: 'Integração PEC', icone: Plug, perfis: ['ADMINISTRADOR'] },
  { para: '/admin/esus', rotulo: 'Integração eSUS', icone: Share2, perfis: ['ADMINISTRADOR'] },
  {
    para: '/admin/sisreg',
    rotulo: 'Integração SISREG',
    icone: Network,
    perfis: ['ADMINISTRADOR'],
  },
  { para: '/admin/smtp', rotulo: 'Servidor de e-mail', icone: Mail, perfis: ['ADMINISTRADOR'] },
  {
    para: '/admin/modelos-email',
    rotulo: 'Modelos de e-mail',
    icone: FileSignature,
    perfis: ['ADMINISTRADOR'],
  },
  { para: '/admin/n8n', rotulo: 'WhatsApp (n8n)', icone: Workflow, perfis: ['ADMINISTRADOR'] },
  {
    para: '/admin/feriados',
    rotulo: 'Feriados',
    icone: CalendarDays,
    perfis: ['ADMINISTRADOR'],
  },
  { para: '/admin/auditoria', rotulo: 'Auditoria', icone: ScrollText, perfis: ['ADMINISTRADOR'] },
  {
    para: '/admin/retencao',
    rotulo: 'Retenção de dados',
    icone: Eraser,
    perfis: ['ADMINISTRADOR'],
  },
  {
    para: '/admin/zona-de-perigo',
    rotulo: 'Zona de perigo',
    icone: TriangleAlert,
    perfis: ['ADMINISTRADOR'],
  },
];

export function AppShell() {
  const { usuario, sair } = useAuth();
  const navegar = useNavigate();
  const [menuAberto, setMenuAberto] = React.useState(false);

  /**
   * Enquanto a gaveta está aberta, o fundo não rola e o Esc fecha.
   *
   * Sem travar a rolagem, o dedo que tenta percorrer o menu arrasta a página
   * atrás dele — e o usuário vê a lista de solicitações passar por baixo de um
   * menu que parecia estar em foco.
   */
  React.useEffect(() => {
    if (!menuAberto) return;

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setMenuAberto(false);
    };

    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', aoTeclar);

    return () => {
      document.body.style.overflow = overflowAnterior;
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [menuAberto]);

  if (!usuario) return null;

  const itens = MENU.filter((item) => item.perfis.includes(usuario.perfil));

  const aoSair = async () => {
    await sair();
    void navegar('/login', { replace: true });
  };

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Permite pular a navegação — exigência básica de acessibilidade. */}
      <a
        href="#conteudo"
        className="bg-marca text-marca-contraste focus:ring-foco sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-4 focus:py-2"
      >
        Pular para o conteúdo
      </a>

      <SemConexao />

      <header className="border-borda bg-cartao sticky top-0 z-30 border-b">
        <div className="area-segura-x flex h-14 items-center gap-3">
          <Botao
            variante="fantasma"
            tamanho="icone"
            className="md:hidden"
            onClick={() => setMenuAberto((aberto) => !aberto)}
            aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={menuAberto}
            aria-controls="menu-lateral"
          >
            {menuAberto ? (
              <X className="size-5" aria-hidden="true" />
            ) : (
              <Menu className="size-5" aria-hidden="true" />
            )}
          </Botao>

          <Link to="/" className="flex items-center gap-2">
            <span className="bg-marca text-marca-contraste flex size-8 items-center justify-center rounded-lg">
              <Stethoscope className="size-4.5" aria-hidden="true" />
            </span>
            <span className="font-semibold tracking-tight">KAVI</span>
          </Link>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm leading-tight font-medium">{usuario.nome}</div>
              <div className="text-silenciado-texto text-xs">{usuario.email}</div>
            </div>
            <Etiqueta tom="marca">{ROTULOS_PERFIL[usuario.perfil]}</Etiqueta>
            <Botao
              variante="fantasma"
              tamanho="icone"
              onClick={aoSair}
              aria-label="Sair do sistema"
            >
              <LogOut className="size-4.5" aria-hidden="true" />
            </Botao>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Fundo escurecido que fecha a gaveta ao toque. Só no celular: no
            desktop o menu é parte do layout, não um sobreposto. */}
        {menuAberto && (
          <div
            data-fundo-menu
            // Decorativo para leitores de tela: quem navega por teclado fecha
            // no Esc ou no próprio botão do cabeçalho, e um segundo controle
            // chamado "Fechar menu" só duplicaria o anúncio.
            aria-hidden="true"
            className="fixed inset-0 top-14 z-30 bg-black/40 md:hidden"
            onClick={() => setMenuAberto(false)}
          />
        )}

        <nav
          id="menu-lateral"
          aria-label="Navegação principal"
          className={cn(
            'border-borda bg-cartao w-60 shrink-0 border-r p-3',
            // No celular a gaveta flutua sobre a página: dividir a largura com
            // o conteúdo deixaria as duas coisas ilegíveis numa tela de 360px.
            'fixed top-14 bottom-0 left-0 z-40 overflow-y-auto',
            'md:static md:top-auto md:bottom-auto md:z-auto md:overflow-visible',
            menuAberto ? 'block' : 'hidden',
            'md:block',
          )}
        >
          <ul className="flex flex-col gap-1">
            {itens.map((item) => {
              const Icone = item.icone;
              return (
                <li key={item.para}>
                  <NavLink
                    to={item.para}
                    end={item.para === '/'}
                    onClick={() => setMenuAberto(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-marca-suave text-marca'
                          : 'text-silenciado-texto hover:bg-silenciado hover:text-fundo-texto',
                      )
                    }
                  >
                    <Icone className="size-4" aria-hidden={true} />
                    {item.rotulo}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="border-borda mt-4 border-t pt-3">
            <NavLink
              to="/trocar-senha"
              onClick={() => setMenuAberto(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-marca-suave text-marca'
                    : 'text-silenciado-texto hover:bg-silenciado hover:text-fundo-texto',
                )
              }
            >
              <KeyRound className="size-4" aria-hidden={true} />
              Trocar senha
            </NavLink>
          </div>
        </nav>

        <main
          id="conteudo"
          className="area-segura-x area-segura-baixo min-w-0 flex-1 py-4 md:px-6 md:py-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
